import type { DocumentHistoryEntry } from "./documentHistoryShared";

export type DocumentHistoryFilter = "all" | "added" | "edited" | "renamed" | "deleted" | "restored";

export const DOCUMENT_HISTORY_FILTERS: DocumentHistoryFilter[] = [
  "all",
  "added",
  "edited",
  "renamed",
  "deleted",
  "restored",
];

export function availableDocumentHistoryFilters(
  entries: DocumentHistoryEntry[]
): DocumentHistoryFilter[] {
  const available = new Set(entries.map((entry) => entry.action));
  return DOCUMENT_HISTORY_FILTERS.filter((filter) => filter === "all" || available.has(filter));
}

interface RelativeTimeCopy {
  justNow: string;
  minutes: string;
  hours: string;
  days: string;
  weeks: string;
  months: string;
}

interface DayCopy {
  today: string;
  yesterday: string;
}

export interface DocumentHistoryDayGroup {
  key: string;
  label: string;
  entries: DocumentHistoryEntry[];
}

export interface TimestampedHistoryEntry {
  createdAt: string;
}

export interface HistoryDayGroup<Entry extends TimestampedHistoryEntry> {
  key: string;
  label: string;
  entries: Entry[];
}

function localeFor(language: string): string {
  return language === "et" ? "et-EE" : "en-GB";
}

function validDate(value: string): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function interpolateCount(template: string, count: number): string {
  return template.replace("{count}", String(count));
}

/** Compact relative time used in the row's right-aligned actor line. */
export function formatDocumentHistoryRelativeTime(
  value: string,
  copy: RelativeTimeCopy,
  now: Date = new Date()
): string {
  const date = validDate(value);
  if (!date) return "";
  const elapsedMs = Math.max(0, now.getTime() - date.getTime());
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) return copy.justNow;
  if (minutes < 60) return interpolateCount(copy.minutes, minutes);
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return interpolateCount(copy.hours, hours);
  const days = Math.floor(hours / 24);
  if (days < 7) return interpolateCount(copy.days, days);
  const weeks = Math.floor(days / 7);
  if (days < 60) return interpolateCount(copy.weeks, weeks);
  return interpolateCount(copy.months, Math.max(1, Math.floor(days / 30)));
}

/** Exact hover timestamp, e.g. "29 May 2026, 11:13". */
export function formatDocumentHistoryExactTime(value: string, language: string): string {
  const date = validDate(value);
  if (!date) return "";
  const locale = localeFor(language);
  const day = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  const time = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
  return `${day}, ${time}`;
}

function calendarDayKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function calendarDayDifference(date: Date, now: Date): number {
  const current = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const target = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.floor((current - target) / 86_400_000);
}

function dayLabel(date: Date, language: string, copy: DayCopy, now: Date): string {
  const difference = calendarDayDifference(date, now);
  if (difference <= 0) return copy.today;
  if (difference === 1) return copy.yesterday;
  const locale = localeFor(language);
  if (difference < 7) {
    return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date);
  }
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

/** Preserve input order while collecting timestamped entries beneath day headers. */
export function groupHistoryEntriesByDay<Entry extends TimestampedHistoryEntry>(
  entries: Entry[],
  language: string,
  copy: DayCopy,
  now: Date = new Date()
): HistoryDayGroup<Entry>[] {
  const groups: HistoryDayGroup<Entry>[] = [];
  for (const entry of entries) {
    const date = validDate(entry.createdAt);
    const key = date ? calendarDayKey(date) : "unknown";
    const existing = groups[groups.length - 1];
    if (existing?.key === key) {
      existing.entries.push(entry);
      continue;
    }
    groups.push({
      key,
      label: date ? dayLabel(date, language, copy, now) : "—",
      entries: [entry],
    });
  }
  return groups;
}

/** Preserve newest-first order while collecting document events beneath day headers. */
export function groupDocumentHistoryByDay(
  entries: DocumentHistoryEntry[],
  language: string,
  copy: DayCopy,
  now: Date = new Date()
): DocumentHistoryDayGroup[] {
  return groupHistoryEntriesByDay(entries, language, copy, now);
}

/**
 * The file-type filter's value for an entry whose name and MIME type say nothing.
 * Lower case and underscored, so no upper-cased extension (`plan.file` → "FILE")
 * can collide with it.
 */
export const UNKNOWN_HISTORY_FILE_TYPE = "__unknown__";

function knownFileType(entry: DocumentHistoryEntry): string | null {
  const leaf = entry.filename.trim().split(/[/\\]/).pop() ?? "";
  const dot = leaf.lastIndexOf(".");
  if (dot > 0 && dot < leaf.length - 1) return leaf.slice(dot + 1).toUpperCase();
  const subtype = entry.contentType?.split("/").pop()?.split(/[.+-]/).pop()?.trim();
  return subtype ? subtype.toUpperCase() : null;
}

/** The type label a row shows, e.g. "PDF"; "FILE" when nothing says. */
export function documentHistoryFileType(entry: DocumentHistoryEntry): string {
  return knownFileType(entry) ?? "FILE";
}

/** The file-type filter's value for an entry: its type, or {@link UNKNOWN_HISTORY_FILE_TYPE}. */
export function documentHistoryFileTypeKey(entry: DocumentHistoryEntry): string {
  return knownFileType(entry) ?? UNKNOWN_HISTORY_FILE_TYPE;
}

/**
 * Prefer a future upload-owned display name, then the current filename. The
 * current schema has neither a display-name column nor an immutable original
 * upload filename, so empty legacy rows receive the explicit untitled fallback.
 */
export function documentHistoryDisplayName(entry: DocumentHistoryEntry): string {
  const displayName = entry.displayName?.trim();
  if (displayName) return displayName;
  const filename = entry.filename.trim();
  if (filename) return filename;
  return `Untitled ${documentHistoryFileType(entry)} - ${entry.version ?? 1}`;
}

export function documentHistoryStorageLabel(entry: DocumentHistoryEntry): string {
  return entry.filePath?.trim() || entry.filename.trim() || `#${entry.documentId}`;
}

function matchesEventFilter(entry: DocumentHistoryEntry, filter: DocumentHistoryFilter): boolean {
  return filter === "all" || entry.action === filter;
}

function historySearchValues(entry: DocumentHistoryEntry): string[] {
  return [
    documentHistoryDisplayName(entry),
    entry.actor.name ?? "",
    entry.filename,
    documentHistoryStorageLabel(entry),
    ...(entry.change?.before ?? []),
    ...(entry.change?.after ?? []),
  ];
}

function matchesHistorySearch(entry: DocumentHistoryEntry, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true;
  return historySearchValues(entry).some((value) =>
    value.toLocaleLowerCase().includes(normalizedQuery)
  );
}

/** "Anyone" in the person filter; every other value is a {@link documentHistoryActorKey}. */
export const ALL_HISTORY_ACTORS = "all";
/** "All file types" in the file-type filter; every other value is a {@link documentHistoryFileTypeKey}. */
export const ALL_HISTORY_FILE_TYPES = "all";

/**
 * Stable identity for the person behind an entry. The profile id when there is
 * one; a name-only actor falls back to the name, and an entry with neither is
 * the single "unknown user" bucket — so the person filter can offer it too.
 */
export function documentHistoryActorKey(entry: DocumentHistoryEntry): string {
  if (entry.actor.id !== null) return `id:${entry.actor.id}`;
  const name = entry.actor.name?.trim();
  return name ? `name:${name}` : UNKNOWN_HISTORY_ACTOR;
}

/** The single person-filter option for everyone the history cannot name. */
export const UNKNOWN_HISTORY_ACTOR = "unknown";

export interface DocumentHistoryActorOption {
  key: string;
  /** Null for the unknown-user bucket; the caller supplies its localised label. */
  name: string | null;
  /** The {@link documentHistoryActorKey}s this option selects. */
  memberKeys: string[];
}

function collectHistoryActorNames(entries: DocumentHistoryEntry[]): Map<string, string | null> {
  const names = new Map<string, string | null>();
  for (const entry of entries) {
    const key = documentHistoryActorKey(entry);
    const name = key === UNKNOWN_HISTORY_ACTOR ? null : entry.actor.name?.trim() || null;
    if (!names.has(key) || (names.get(key) === null && name !== null)) names.set(key, name);
  }
  return names;
}

function compareHistoryActors(
  a: DocumentHistoryActorOption,
  b: DocumentHistoryActorOption,
  collator: Intl.Collator
): number {
  return (
    collator.compare(a.name ?? "", b.name ?? "") || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
  );
}

/**
 * Everyone who appears in the loaded history, alphabetically, with one
 * "unknown user" option last. A profile's name is taken from whichever of its
 * entries carries one — an earlier entry whose profile join came back empty
 * must not label the person "Unknown user" for good. Profiles that no loaded
 * entry names join the unknown option: two options both reading "Unknown user"
 * would filter to different people with no way to tell which is which.
 */
export function availableDocumentHistoryActors(
  entries: DocumentHistoryEntry[],
  language: string
): DocumentHistoryActorOption[] {
  const names = collectHistoryActorNames(entries);
  const named: DocumentHistoryActorOption[] = [];
  const nameless: string[] = [];
  for (const [key, name] of names) {
    if (name === null) nameless.push(key);
    else named.push({ key, name, memberKeys: [key] });
  }
  const collator = new Intl.Collator(localeFor(language), { sensitivity: "base" });
  named.sort((a, b) => compareHistoryActors(a, b, collator));
  if (nameless.length === 0) return named;
  return [...named, { key: UNKNOWN_HISTORY_ACTOR, name: null, memberKeys: nameless.sort() }];
}

/** Milliseconds from `now` until the next local midnight. */
export function msUntilNextLocalDay(now: Date = new Date()): number {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return Math.max(1, next.getTime() - now.getTime());
}

/** The local calendar day `date` falls on, as `YYYY-MM-DD`. */
export function documentHistoryDayKey(date: Date): string {
  return calendarDayKey(date);
}

/** File types present in the loaded history, alphabetically. */
export function availableDocumentHistoryFileTypes(entries: DocumentHistoryEntry[]): string[] {
  return [...new Set(entries.map(documentHistoryFileTypeKey))].sort();
}

export type DocumentHistoryDatePreset = "today" | "7d" | "30d";

/**
 * A date filter as the user chose it. Presets are kept as presets and resolved
 * against the clock at filter time, so "Today" left open past midnight still
 * means today. A custom range holds local calendar days, both inclusive.
 */
export type DocumentHistoryDateFilter =
  | { kind: "preset"; preset: DocumentHistoryDatePreset }
  | { kind: "custom"; from: Date; to: Date };

const PRESET_DAYS: Record<DocumentHistoryDatePreset, number> = { today: 1, "7d": 7, "30d": 30 };

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addLocalDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/**
 * Half-open `[from, to)` bounds. A preset ends at the close of today, not at
 * `now`: a row stamped by the server a little ahead of a lagging browser clock
 * still happened today, and must not vanish from "Today".
 */
export function documentHistoryDateBounds(
  filter: DocumentHistoryDateFilter,
  now: Date = new Date()
): { from: Date; to: Date } {
  if (filter.kind === "preset") {
    const today = startOfLocalDay(now);
    return {
      from: addLocalDays(today, -(PRESET_DAYS[filter.preset] - 1)),
      to: addLocalDays(today, 1),
    };
  }
  const [start, end] =
    filter.from <= filter.to ? [filter.from, filter.to] : [filter.to, filter.from];
  return { from: startOfLocalDay(start), to: addLocalDays(startOfLocalDay(end), 1) };
}

/** Narrowing beyond the search box and the event type. Absent fields do not filter. */
export interface DocumentHistoryRefinements {
  /** An option's {@link DocumentHistoryActorOption.memberKeys}; absent or empty means anyone. */
  actorKeys?: readonly string[];
  fileType?: string;
  dateFilter?: DocumentHistoryDateFilter | null;
}

function matchesRefinements(
  entry: DocumentHistoryEntry,
  refinements: DocumentHistoryRefinements,
  bounds: { from: Date; to: Date } | null
): boolean {
  const { actorKeys, fileType } = refinements;
  if (actorKeys?.length && !actorKeys.includes(documentHistoryActorKey(entry))) return false;
  if (
    fileType &&
    fileType !== ALL_HISTORY_FILE_TYPES &&
    documentHistoryFileTypeKey(entry) !== fileType
  ) {
    return false;
  }
  return matchesHistoryDate(entry.createdAt, bounds);
}

function matchesHistoryDate(createdAt: string, bounds: { from: Date; to: Date } | null): boolean {
  if (!bounds) return true;
  const date = validDate(createdAt);
  if (!date) return false;
  return date >= bounds.from && date < bounds.to;
}

export function filterDocumentHistoryEntries(
  entries: DocumentHistoryEntry[],
  query: string,
  filter: DocumentHistoryFilter,
  refinements: DocumentHistoryRefinements = {},
  now: Date = new Date()
): DocumentHistoryEntry[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const bounds = refinements.dateFilter
    ? documentHistoryDateBounds(refinements.dateFilter, now)
    : null;
  return entries.filter(
    (entry) =>
      matchesEventFilter(entry, filter) &&
      matchesHistorySearch(entry, normalizedQuery) &&
      matchesRefinements(entry, refinements, bounds)
  );
}
